import React, { useState } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PagePaper } from 'dashboard/components/common';

/**
 * Setup checklist tile. The backend computes the items + done/total
 * counts from existing brand state; here we just render them with a
 * tappable row per item and a progress bar at the top.
 *
 * When everything's done the card hides itself on user dismiss so the
 * empty "all done" state doesn't waste vertical space forever.
 */
export default function ChecklistCard({ checklist, onJump }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!checklist) return null;
  const allDone = checklist.done === checklist.total && checklist.total > 0;
  if (allDone && collapsed) return null;

  return (
    <PagePaper sx={{ p: '18px', borderRadius: '16px' }}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        mb='10px'
        flexWrap='wrap'
        gap='10px'
      >
        <Box>
          <Typography fontWeight='700'>
            {allDone ? 'Your brand is set up' : 'Finish setting up your brand'}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {checklist.done} of {checklist.total} done · {checklist.percent}%
          </Typography>
        </Box>
        {allDone && (
          <Button size='small' onClick={() => setCollapsed(true)}>
            Hide
          </Button>
        )}
      </Stack>
      <LinearProgress
        variant='determinate'
        value={checklist.percent || 0}
        color={allDone ? 'success' : 'primary'}
        sx={{ borderRadius: 999, mb: '12px', height: 4 }}
      />
      <Stack spacing='8px'>
        {(checklist.items || []).map((item) => (
          <Stack
            key={item.key}
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            onClick={() => item.link && onJump?.(item.link)}
            sx={{
              p: '10px',
              borderRadius: '10px',
              borderTop: '1px solid',
              borderColor: 'divider',
              cursor: item.link ? 'pointer' : 'default',
              '&:hover': item.link ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <Stack direction='row' spacing='10px' alignItems='flex-start' flexWrap='wrap'>
              <CheckCircleIcon
                fontSize='small'
                sx={{
                  color: item.done ? 'success.main' : 'action.disabled',
                  mt: '2px',
                }}
              />
              <Box>
                <Typography
                  variant='body2'
                  fontWeight={item.done ? 500 : 600}
                  sx={{
                    textDecoration: item.done ? 'line-through' : 'none',
                    opacity: item.done ? 0.7 : 1,
                  }}
                >
                  {item.title}
                </Typography>
                {item.hint && (
                  <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
                    {item.hint}
                  </Typography>
                )}
              </Box>
            </Stack>
            {item.link && !item.done && (
              <Button
                size='small'
                variant='outlined'
                onClick={(e) => { e.stopPropagation(); onJump?.(item.link); }}
              >
                Open
              </Button>
            )}
          </Stack>
        ))}
      </Stack>
    </PagePaper>
  );
}
